import React, { useMemo, useState } from 'react'
import { FlightParameters, FlightSearchResults, Journey } from '../../api'
import { calculateTotalFare, generateFlightInfoParams } from '../../utils/FlightUtil'
import FlexibleDateTabs from './FlexibleDateTabs'
import JourneyCard from './JourneyCard'

interface FlightCardProps {
  flightSearchParameters: FlightParameters
  messageText: string
  flightSearchResults: FlightSearchResults
}

const FlightCardList: React.FC<FlightCardProps> = ({
  messageText,
  flightSearchParameters,
  flightSearchResults,
}) => {
  // State for flexible search date selection
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    if (flightSearchResults.isFlexibleSearch && flightSearchResults.groupedFlights?.length > 0) {
      return flightSearchResults.groupedFlights[0].departureDate
    }
    return ''
  })

  const getFlightInfoUrl = (journeys: Journey[]): string => {
    const baseUrl = 'https://dev.kyte.travel/flights/info'
    const params = Object.entries(generateFlightInfoParams(journeys, flightSearchParameters))
      .map(([key, value]) => `${key}=${value}`)
      .join('&&')

    return `${baseUrl}?${params}`
  }

  // Determine which flights to display
  const displayFlights = useMemo(() => {
    if (flightSearchResults.isFlexibleSearch && flightSearchResults.groupedFlights) {
      const selectedGroup = flightSearchResults.groupedFlights.find(
        group => group.departureDate === selectedDate,
      )
      return selectedGroup?.flights || []
    }
    return flightSearchResults.flights || []
  }, [flightSearchResults, selectedDate])

  return (
    <div
      className="chat-card"
      style={{
        maxWidth: '90%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ marginBottom: '10px' }}>{messageText}</div>

      {/* Render date tabs for flexible search */}
      {flightSearchResults.isFlexibleSearch && flightSearchResults.groupedFlights && (
        <FlexibleDateTabs
          groupedFlights={flightSearchResults.groupedFlights}
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
        />
      )}

      <div
        style={{
          overflowX: 'auto',
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          paddingBottom: '10px',
        }}
      >
        {displayFlights.map((flight, index) => {
          const totalFare = useMemo(() => calculateTotalFare(flight.journeys), [flight.journeys])

          return (
            <a
              key={flight.id || index}
              href={getFlightInfoUrl(flight.journeys)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                border: '1px solid var(--vela-border-color)',
                borderRadius: '6px',
                padding: '10px',
                backgroundColor: 'white',
                minWidth: '300px',
                flex: '0 0 auto',
                cursor: 'pointer',
              }}
            >
              {flight.journeys.map((journey, idx) => (
                <JourneyCard
                  key={journey.journeyKey || idx}
                  journey={journey}
                  isLast={idx === flight.journeys.length - 1}
                />
              ))}

              <div
                style={{
                  marginTop: '12px',
                  padding: '8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  fontWeight: 'bold',
                  color: 'var(--vela-primary-color)',
                  borderTop: '1px solid #ddd',
                }}
              >
                {`₩ ${totalFare.toLocaleString()}`}
              </div>
            </a>
          )
        })}
      </div>
    </div>
  )
}

export default FlightCardList
